import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {BarAmountList, DotAmountList} from '../../../List';
import {Divider} from 'antd';
import {
  StyledSpendingDetails,
  StyledSpendingDetailsCardExtra,
  StyledSpendingDetailsMoreButton,
  StyledSpendingDetailsOverflowSpace,
} from './StyledSpendingDetails';
import {StyledOverviewPageCard} from '../../../../pages/OverviewPage/StyledOverviewPage';
import SpinSmall from '../../../SpinSmall';
import Counter from '../Counter';
import UpcomingSpending from '../UpcomingSpending';
import Variations from '../Variations';
import ScaleFade from '../../../animations/ScaleFade';
import routes from '../../../../routes/routes.json';

const subscriptionsListRoute = routes.subscriptionsList;

const defaultDetails = {
  data: [],
  isEnableMoreButton: false,
  moreButtonCounter: 0
}

const defaultMaxDisplayedItems = 5;
const moreButtonDisplayedItemsStep = 50;


const SpendingDetails = ({
  categories = [],
  costCenter = [],
  loading = false,
  variations,
  upcoming,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [variationsDetails, setVariationsDetails] = useState(defaultDetails);
  const [upcomingDetails, setUpcomingDetails] = useState(defaultDetails);

  const navigate = useNavigate();

  const getCostCenterExtraCounter = (data) => {
    let extra;
    let dataLength = data.filter(d => d.actual > d.expected).length;
    if (Boolean(dataLength)) {
      extra = <Counter variant='error' value={`+${dataLength}`} />;
    }
    return extra;
  }

  const getVariationsExtraCounter = ({data, emptyText = '', loading}) => {
    let extra;
    let dataLength = data.length;
    let aboveLength = data.filter(d => d.actual > d.budget).length;
    let belowLength = data.filter(d => d.actual < d.budget).length;

    if (!loading) {
      if (Boolean(aboveLength)) {
        extra = <Counter variant={'error'} value={`+${aboveLength}`} />;
      } else if (!Boolean(aboveLength) && Boolean(belowLength)) {
        extra = <Counter variant={'success'} value={belowLength} />;
      } else if (!Boolean(dataLength)) {
        extra = <StyledSpendingDetailsCardExtra>{emptyText}</StyledSpendingDetailsCardExtra>;
      }
    }
    return extra;
  }

  const updateDetails = ({data, itemsCount = defaultMaxDisplayedItems, setDetails}) => {
    const dataLength = data.length;
    const isEnableMoreButton = data.length > itemsCount;
    setDetails({
      data: data.slice(0, itemsCount),
      isEnableMoreButton,
      moreButtonCounter: isEnableMoreButton ? dataLength - itemsCount : 0
    });
  }

  useEffect(() => {
    updateDetails({data: variations, setDetails: setVariationsDetails});
  }, [variations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateDetails({data: upcoming, setDetails: setUpcomingDetails});
  }, [upcoming]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUpcomingExtraCounter = ({data, emptyText = '', loading}) => {
    let extra;
    let dataLength = data.length;
    const isNotEmptyData = Boolean(dataLength);

    if (!loading) {
      if (isNotEmptyData) {
        extra = <Counter variant={'success'} value={dataLength} />;
      } else if (!isNotEmptyData) {
        extra = <StyledSpendingDetailsCardExtra>{emptyText}</StyledSpendingDetailsCardExtra>;
      }
    }
    return extra;
  }

  const getMoreButton = ({data, details, setDetails}) => {
    const displayedData = details?.data || [];
    const dataLength = data.length;
    const displayedDataLength = displayedData.length;
    const unVisibleDataLength = dataLength - displayedDataLength;
    const moreCounter = unVisibleDataLength > moreButtonDisplayedItemsStep ? moreButtonDisplayedItemsStep : unVisibleDataLength;

    if (unVisibleDataLength <= 0) return ;

    return (
      <StyledSpendingDetailsMoreButton
        onClick={() => handleMoreClick({data, details, setDetails})}
        type='link'
      >
        {t('more')} (+{moreCounter})
      </StyledSpendingDetailsMoreButton>
    );
  }

  const handleMoreClick = ({data, details, setDetails}) => {
    const displayedData = details?.data || [];
    const displayedDataLength = displayedData.length;
    const itemsCount = displayedDataLength + moreButtonDisplayedItemsStep;
    updateDetails({data, itemsCount, setDetails});
  }

  const variationsExtra = useMemo(() => getVariationsExtraCounter({
    data: variations,
    emptyText: t('noVariations'),
    loading
  }), [loading, t, variations]); // eslint-disable-line react-hooks/exhaustive-deps

  const upcomingSpendingExtra = useMemo(() => getUpcomingExtraCounter({
    data: upcoming,
    emptyText: t('noSpending'),
    loading
  }), [loading, t, upcoming]); // eslint-disable-line react-hooks/exhaustive-deps


  const costCenterExtra = useMemo(() => getCostCenterExtraCounter(costCenter), [costCenter]);

  const variationMoreButton = getMoreButton({data: variations, details: variationsDetails, setDetails: setVariationsDetails});

  const upcomingMoreButton = getMoreButton({data: upcoming, details: upcomingDetails, setDetails: setUpcomingDetails});

  const handleOpenSubscriptionPage = (e, item) => {
    let subscriptionId = item?.subscription_id;
    if (subscriptionId) navigate(`${subscriptionsListRoute}/${subscriptionId}`);
  }

  const handleOnCostCenterClick = (e, item) => navigate(`${subscriptionsListRoute}#authorized?tags=${item?.name}`);

  return (
    <StyledSpendingDetails
      direction='vertical'
      size='middle'
      {...rest}
    >
      <StyledSpendingDetailsOverflowSpace
        direction='vertical'
        size='middle'
      >
        <SpinSmall spinning={loading}>
          <StyledSpendingDetails
            direction='vertical'
            size='small'
          >
            {categories.length ? (
              <ScaleFade className='w-100'>
                <StyledOverviewPageCard
                  title={t('categories')}
                  variant='outlined'
                >
                  {<DotAmountList items={categories} />}
                </StyledOverviewPageCard>
                {costCenter.length && <Divider size='small' />}
              </ScaleFade>
            ) : ''}
            {costCenter.length ? (
              <ScaleFade className='w-100'>
                <StyledOverviewPageCard
                  extra={costCenterExtra}
                  title={t('costCenters')}
                  variant='outlined'
                >
                  <BarAmountList items={costCenter} onItemClick={handleOnCostCenterClick} />
                </StyledOverviewPageCard>
              </ScaleFade>
            ) : ''}
            <Divider size='small' />
            <StyledOverviewPageCard
              extra={variationsExtra}
              title={t('variations')}
              variant='outlined'
            >
              <Variations
                data={variationsDetails.data}
                handleButtonClick={handleOpenSubscriptionPage}
              />
              {variationMoreButton}
            </StyledOverviewPageCard>
            <Divider size='middle' />
            <StyledOverviewPageCard
              extra={upcomingSpendingExtra}
              title={t('upcomingSpending')}
              variant='outlined'
            >
              <UpcomingSpending data={upcomingDetails.data} />
              {upcomingMoreButton}
            </StyledOverviewPageCard>
          </StyledSpendingDetails>
        </SpinSmall>
      </StyledSpendingDetailsOverflowSpace>
    </StyledSpendingDetails>
  );
}

SpendingDetails.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number
    })
  ),
  costCenter: PropTypes.arrayOf(
    PropTypes.shape({
      actual: PropTypes.number,
      expected: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  variations: PropTypes.arrayOf(
    PropTypes.shape({
      actual: PropTypes.number,
      budget: PropTypes.number,
      expected: PropTypes.number,
      service: PropTypes.shape({
        logo: PropTypes.string,
        name: PropTypes.string
      }),
      subscription_id: PropTypes.string
    })
  ),
  upcoming: PropTypes.arrayOf(
    PropTypes.shape({
      actual: PropTypes.number,
      budget: PropTypes.number,
      expected: PropTypes.number,
      service: PropTypes.shape({
        logo: PropTypes.string,
        name: PropTypes.string
      }),
      subscription_id: PropTypes.string
    })
  )
}

export default SpendingDetails;
